import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import useCopy from "@/hooks/useCopy";
import { useCreateWebhook, useDeleteWebhook } from "@/queries";
import { displayErrorToast } from "@/utils/toast";
import { XIcon } from "@heroicons/react/outline";
import { Check, Copy } from "lucide-react";
import { FC, FormEvent, useState } from "react";

const WebhookForm: FC = () => {
  const [url, setUrl] = useState("");
  const auth = useAuth();
  const userContext = useUser();
  const userToken = auth!.userToken;
  const createWorkspaceWebhook = useCreateWebhook(userToken!);
  const [openWebhookSecretModal, setOpenWebhookSecretModal] = useState(false);
  const [secretSignature, setSecretSignature] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const newWebhook = await createWorkspaceWebhook.mutateAsync({
        url,
        workspace_id: userContext?.activeWorkspaceId!,
      });
      if (newWebhook) {
        userContext.workspaceWebhooks.push(newWebhook);
        setOpenWebhookSecretModal(true);
        setSecretSignature(newWebhook.webhook.secret_key);
        setUrl("");
      }
    } catch (error) {
      displayErrorToast(error);
      console.error(error);
    }
  };

  const isDisabled = () => {
    return createWorkspaceWebhook.isLoading || url.trim() === "";
  };
  const renderForm = () => (
    <form onSubmit={handleSubmit} className="mt-6 space-y-4">
      <div>
        <label
          className="block text-sm font-medium text-gray-700"
          htmlFor="url"
        >
          Webhook URL
        </label>
        <input
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
          id="url"
          name="url"
          onChange={(e) => setUrl(e.target.value)}
          required
          type="url"
          placeholder="https://example.com/promptlayer-webhook"
          value={url}
        />
      </div>
      <div className="inline-flex items-center">
        <Button
          disabled={isDisabled()}
          isLoading={createWorkspaceWebhook.isLoading}
          type="submit"
        >
          Add Webhook
        </Button>
      </div>
    </form>
  );

  const renderWebhookDetails = () => (
    <div>
      <div className="my-4 border-b border-gray-300 pb-2 text-base font-medium text-gray-800">
        Webhooks:
      </div>
      <div className="w-full space-y-4">
        <Webhooks />
      </div>
    </div>
  );

  return (
    <>
      <div className="mt-2">
        <a
          href="https://docs.promptlayer.com/features/prompt-registry#setting-up-a-webhook"
          target="_blank"
          rel="noreferrer"
          className="text-sm text-blue-500 hover:text-blue-400"
        >
          Learn more about webhooks here.
        </a>
      </div>
      <div className="max-w-lg px-2">{renderWebhookDetails()}</div>
      <div className="max-w-lg px-2">{renderForm()}</div>
      <WebhookSecretModal
        open={openWebhookSecretModal}
        setOpen={setOpenWebhookSecretModal}
        secretSignature={secretSignature}
      />
    </>
  );
};

function Webhooks() {
  const userContext = useUser();
  if (userContext.workspaceWebhooks.length === 0)
    return (
      <div className="text-base text-gray-600">No webhooks available.</div>
    );

  return (
    <div className="space-y-3">
      {userContext?.workspaceWebhooks.map((webhook) => (
        <Webhook key={webhook.id} webhook={webhook} />
      ))}
    </div>
  );
}

function Webhook({ webhook }: { webhook: any }) {
  const auth = useAuth();
  const deleteWorkspaceWebhook = useDeleteWebhook(auth?.userToken!);
  const userContext = useUser();
  const { copy, isCopying } = useCopy();
  const handleDelete = async (webhookId: number) => {
    try {
      await deleteWorkspaceWebhook.mutateAsync(webhookId);
      userContext.workspaceWebhooks = userContext.workspaceWebhooks.filter(
        (webhook) => webhook.id !== webhookId,
      );
    } catch (error) {
      displayErrorToast(error);
      console.error(error);
    }
  };

  const handleCopy = (text: string) => {
    copy(text);
  };

  return (
    <div key={webhook.id} className="rounded-md border bg-white p-3 shadow-sm">
      <div className="mb-2 flex items-start justify-between">
        <div className="flex-1 break-all">
          <p className="text-sm font-medium text-gray-700">Webhook URL</p>
          <p className="text-sm text-gray-600">{webhook.url}</p>
        </div>
        <Button
          variant="ghost"
          onClick={() => handleDelete(webhook.id)}
          size="sm"
          className="-mr-1 -mt-1 text-red-500 hover:bg-red-50 hover:text-red-700"
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </div>

      <div className="mt-3">
        <p className="mb-1 text-sm font-medium text-gray-700">
          Secret Signature
        </p>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="password"
              value={webhook.secret_key}
              readOnly
              className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 pr-10 text-sm"
            />
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-2"
              onClick={() => handleCopy(webhook.secret_key)}
            >
              {isCopying ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <Copy className="h-3.5 w-3.5 text-gray-500" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface WebhookModalProps {
  secretSignature: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}

function WebhookSecretModal({
  secretSignature,
  open,
  setOpen,
}: WebhookModalProps) {
  const { copy, isCopying } = useCopy();

  const handleCopy = () => {
    copy(secretSignature);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Webhook Secret Signature</DialogTitle>
          <DialogDescription>
            Copy and save this secret signature. You will need it to verify
            webhook requests.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="relative">
            <div className="flex items-center justify-between rounded-md border p-3 shadow-sm">
              <code className="break-all font-mono text-sm">
                {secretSignature}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                className="ml-2 h-8 px-2"
              >
                {isCopying ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium">How to use this secret:</h4>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>
                Use this secret to verify the signature in the webhook payload
              </li>
              <li>
                The signature is included in the{" "}
                <code className="text-xs">X-PromptLayer-Signature</code> header
              </li>
              <li>
                Verify the signature to ensure the webhook request is authentic
              </li>
            </ul>
          </div>
        </div>
        <div className="flex justify-end">
          <DialogClose>
            <Button>Close</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default WebhookForm;
